/**
 * Generic social stats hook for any data source
 */

import { useState, useMemo } from 'react';
import type {
  SocialStatsDataAdapter,
  SocialStatsHookReturn,
  SocialStatsDataPoint
} from '@/types/social-stats';
import { cleanSocialStatsData } from '@/utils/social-stats';

/**
 * Generic hook for social stats functionality
 * @param adapter - Data adapter for the specific platform
 * @param isLoading - Loading state
 * @param source - Raw data source
 * @param defaultTab - Default tab to show
 * @returns Social stats hook properties
 */
export const useSocialStats = <T>(
  adapter: SocialStatsDataAdapter<T>,
  isLoading: boolean,
  source?: T | null,
  defaultTab?: string
): SocialStatsHookReturn => {
  const tabs = useMemo(() => adapter.getTabs(), [adapter]);
  const defaultTabKey = useMemo(() => defaultTab || adapter.getDefaultTab(), [adapter, defaultTab]);

  const [activeSocialStatTab, setActiveSocialStatTab] = useState<string>(() => {
    // Validate that defaultTabKey exists in the list of tab keys
    const validTabKeys = tabs.map((tab) => tab.key);
    if (validTabKeys.includes(defaultTabKey)) {
      return defaultTabKey;
    }
    // Fallback: use the first tab's key if available, otherwise an empty string
    return validTabKeys[0] || '';
  });

  const tabData = useMemo(() => {
    if (!source) {
      return tabs.reduce((acc, tab) => {
        acc[tab.key] = [];
        return acc;
      }, {} as Record<string, SocialStatsDataPoint[]>);
    }

    const result: Record<string, SocialStatsDataPoint[]> = {};

    // Extract data for each tab using the unified adapter method
    tabs.forEach((tab) => {
      let rawData: SocialStatsDataPoint[] = [];
      rawData = adapter.getDataForTab(source, tab.key);
      try {
        result[tab.key] = cleanSocialStatsData(rawData);
      } catch (error) {
        // Removed console.error logging to comply with coding guidelines
        result[tab.key] = [];
      }
    });

    return result;
  }, [adapter, source, tabs]);

  return {
    activeSocialStatTab,
    setActiveSocialStatTab,
    isLoading,
    tabs,
    tabData,
    sectionTitle: adapter.getSectionTitle(),
    sectionIconBgColor: adapter.getSectionIconBgColor(),
  };
};
